import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  UserCircle,
  ClipboardList,
  Building2,
  BarChart3,
  DollarSign,
  Clock,
  BookOpen,
  FolderOpen,
  CalendarCheck,
  RefreshCw,
  Mail,
  Bell,
  MoreHorizontal,
  Shield,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Layout = ({ children }) => {
  const { user, logout, isPartner, isManager } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [newEnquiriesCount, setNewEnquiriesCount] = useState(0);

  // Fetch new enquiries count
  useEffect(() => {
    const fetchEnquiriesCount = async () => {
      if (user?.role === 'partner' || user?.role === 'manager') {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/api/enquiries/count`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setNewEnquiriesCount(data.new);
          }
        } catch (error) {
          console.error('Error fetching enquiries count:', error);
        }
      }
    };
    
    fetchEnquiriesCount();
    const interval = setInterval(fetchEnquiriesCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  // Close more menu on outside click
  useEffect(() => {
    if (!moreMenuOpen) return;
    const close = () => setMoreMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [moreMenuOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['partner', 'manager', 'staff'] },
    { name: 'Tasks', href: '/tasks', icon: ClipboardList, roles: ['partner', 'manager', 'staff'] },
    { name: 'Recurring Tasks', href: '/recurring-tasks', icon: RefreshCw, roles: ['partner', 'manager'] },
    { name: 'Clients', href: '/clients', icon: Building2, roles: ['partner', 'manager'] },
    { name: 'Billing', href: '/billing', icon: DollarSign, roles: ['partner', 'manager'] },
    { name: 'Masters', href: '/masters', icon: Settings, roles: ['partner', 'manager'] },
    { name: 'Workflows', href: '/workflows', icon: BookOpen, roles: ['partner', 'manager', 'staff'] },
    { name: 'Time Tracking', href: '/time-tracking', icon: Clock, roles: ['partner', 'manager'] },
    { name: 'File Registry', href: '/file-registry', icon: FolderOpen, roles: ['partner', 'manager', 'staff'] },
    { name: 'Users', href: '/users', icon: Users, roles: ['partner'] },
    { name: 'Enquiries', href: '/enquiries', icon: Mail, roles: ['partner', 'manager'], badge: newEnquiriesCount },
    { name: 'Reminders', href: '/reminders', icon: Bell, roles: ['partner', 'manager', 'staff'] },
    { name: 'Internal Audit', href: '/audit', icon: Shield, roles: ['partner', 'manager', 'staff'] },
    { name: 'Reports', href: '/reports', icon: BarChart3, roles: ['partner', 'manager'] },
    { name: 'Book a Call', href: '/book-call', icon: CalendarCheck, roles: ['partner', 'manager', 'staff'] },
  ];

  const filteredNavigation = navigation.filter((item) =>
    item.roles.includes(user?.role)
  );

  // Bottom nav: show top 4 most used + More
  const bottomNavItems = filteredNavigation.slice(0, 4);
  const moreNavItems = filteredNavigation.slice(4);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar (hamburger) */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)}></div>
        <div className="fixed inset-y-0 left-0 flex flex-col w-72 bg-white shadow-xl">
          <div className="flex items-center justify-between h-16 px-4 border-b">
            <div className="flex items-center gap-2">
              <img src="/images/ca-logo.png" alt="CA" className="h-8 w-auto" />
              <div className="flex flex-col">
                <span className="text-[10px] font-semibold text-gray-600 tracking-wide">HYDER ALI AND ASSOCIATES</span>
                <span className="text-lg font-bold text-blue-600">OfficeCRM</span>
              </div>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="p-1">
              <X className="w-6 h-6" />
            </button>
          </div>
          <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-lg ${
                    isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                  {item.name}
                  {item.badge > 0 && (
                    <span className="ml-auto bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
          {/* User info at bottom of sidebar */}
          <div className="border-t p-3">
            <Link to="/profile" onClick={() => setSidebarOpen(false)} className="flex items-center p-2 rounded-lg hover:bg-gray-100">
              <UserCircle className="w-8 h-8 text-gray-400 mr-3" />
              <div>
                <p className="text-sm font-medium">{user?.full_name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </Link>
            <button onClick={handleLogout} className="w-full flex items-center px-2 py-2 mt-1 text-sm text-red-600 hover:bg-red-50 rounded-lg">
              <LogOut className="w-4 h-4 mr-3" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 min-h-0 bg-white border-r">
          <div className="flex items-center h-20 px-4 border-b">
            <div className="flex items-center gap-3">
              <img src="/images/ca-logo.png" alt="CA" className="h-10 w-auto" />
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-gray-700 tracking-wide">HYDER ALI AND ASSOCIATES</span>
                <span className="text-xl font-bold text-blue-600">OfficeCRM</span>
              </div>
            </div>
          </div>
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg ${
                    isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <item.icon className="w-5 h-5 mr-3" />
                  {item.name}
                  {item.badge > 0 && (
                    <span className="ml-auto bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 pb-20 lg:pb-0">
        {/* Top navbar */}
        <div className="sticky top-0 z-10 flex h-14 lg:h-16 bg-white border-b">
          <button className="px-3 text-gray-500 lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button">
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center justify-between flex-1 px-3 lg:px-4">
            <div>
              <h1 className="text-base lg:text-lg font-semibold text-gray-900 truncate">
                {navigation.find((item) => item.href === location.pathname)?.name || 'OfficeCRM'}
              </h1>
            </div>
            <div className="flex items-center space-x-2 lg:space-x-4">
              <Link 
                to="/profile" 
                className="flex items-center space-x-2 hover:bg-gray-100 p-1.5 lg:p-2 rounded-lg transition-colors"
                title="My Profile"
              >
                <UserCircle className="w-7 h-7 lg:w-8 lg:h-8 text-gray-400" />
                <div className="hidden md:block">
                  <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                  <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                </div>
              </Link>
              <button
                onClick={handleLogout}
                className="hidden lg:flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-3 lg:p-6">{children}</main>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t z-30 lg:hidden safe-bottom" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around h-14">
          {bottomNavItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center justify-center flex-1 h-full relative ${
                  isActive ? 'text-blue-600' : 'text-gray-500'
                }`}
                data-testid={`bottom-nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-[10px] mt-0.5 font-medium">{item.name}</span>
                {item.badge > 0 && (
                  <span className="absolute top-1 right-1/4 bg-red-500 text-white text-[8px] w-4 h-4 flex items-center justify-center rounded-full">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
          {/* More button */}
          {moreNavItems.length > 0 && (
            <div className="flex flex-col items-center justify-center flex-1 h-full relative">
              <button
                onClick={(e) => { e.stopPropagation(); setMoreMenuOpen(!moreMenuOpen); }}
                className={`flex flex-col items-center justify-center w-full h-full ${moreMenuOpen ? 'text-blue-600' : 'text-gray-500'}`}
                data-testid="bottom-nav-more"
              >
                <MoreHorizontal className="w-5 h-5" />
                <span className="text-[10px] mt-0.5 font-medium">More</span>
              </button>
              {/* More menu popup */}
              {moreMenuOpen && (
                <div className="absolute bottom-full right-0 mb-2 w-56 bg-white border rounded-lg shadow-xl max-h-80 overflow-y-auto">
                  {moreNavItems.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMoreMenuOpen(false)}
                        className={`flex items-center px-4 py-3 text-sm border-b last:border-b-0 ${
                          isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <item.icon className="w-4 h-4 mr-3 flex-shrink-0" />
                        {item.name}
                        {item.badge > 0 && (
                          <span className="ml-auto bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Layout;
